import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from "react"
import { Search, X } from "lucide-react"
import DropdownFilterChip from "./DropdownFilterChip.jsx"
import TextFilterChip from "./TextFilterChip.jsx"
import {
    FILTER_CHIP_REMOVE_GAP_PX,
    FILTER_CHIP_REMOVE_ICON_SIZE_PX,
    FILTER_CHIP_REMOVE_PADDING_RIGHT_PX,
    FILTER_CHIP_ROW_HEIGHT_PX,
    SEARCH_BAR_HEIGHT,
} from "./filterChipStyles.js"

/**
 * @typedef {Object} SearchBarChip
 * @property {string} label
 * @property {string} [displayLabel]
 * @property {string} [command]
 * @property {string} [value]
 * @property {"text" | "dropdown"} [chipType]
 * @property {Array<{ display: string, value: string }>} [options]
 */

const SearchBar = forwardRef(({
    className = "",
    placeholder = "Search...",
    initialChips = [],
    initialFreeText = "",
    onSearch = () => {},
    onAddChip = null,
}, ref) => {
    /** @type {[SearchBarChip[], Function]} */
    const [chips, setChips] = useState(initialChips)
    const [inputText, setInputText] = useState(initialFreeText)
    const [focusedElement, setFocusedElement] = useState(/** @type {number | "input" | null} */(null))
    const inputRef = useRef(/** @type {HTMLInputElement | null} */(null))
    const chipRefs = useRef(/** @type {Array<any>} */([]))
    /** @type {React.MutableRefObject<{ index: number, chip: SearchBarChip } | null>} */
    const pendingChipFocusRef = useRef(null)

    function compileChipsToSearchString(chipList, freeText) {
        let searchString = ""
        chipList.forEach((chip, index) => {
            if (chip.chipType === "dropdown") {
                const chipValue = chip.value ?? ""
                if (chipValue) {
                    searchString += `{${chip.label} = ${chipValue}}`
                }
                return
            }
            const chipRef = chipRefs.current[index]
            if (chipRef?.getSearchString) {
                searchString += chipRef.getSearchString()
            }
        })
        if (freeText.trim() !== "") {
            searchString += freeText.trim()
        }
        return searchString.trim()
    }

    useEffect(() => {
        setChips(initialChips)
        setInputText(initialFreeText)
    }, [initialChips, initialFreeText])

    const compileSearchString = useCallback(() => {
        return compileChipsToSearchString(chips, inputText)
    }, [chips, inputText])

    const submitSearch = useCallback(() => {
        onSearch(compileSearchString())
    }, [compileSearchString, onSearch])

    const handleDropdownChipValueChange = useCallback((chipIndex, newValue) => {
        setChips((prevChips) => {
            const nextChips = prevChips.map((chip, index) => (
                index === chipIndex ? { ...chip, value: newValue } : chip
            ))
            queueMicrotask(() => {
                onSearch(compileChipsToSearchString(nextChips, inputText))
            })
            return nextChips
        })
    }, [inputText, onSearch])

    useImperativeHandle(ref, () => ({
        submit: submitSearch,
        compileSearchString,
    }))

    function focusChip(chipIndex) {
        document.querySelector(
            `[data-chip-row-index="${chipIndex}"] [data-chip-focus-target]`
        )?.focus()
    }

    const removeChipAtIndex = useCallback((chipIndex) => {
        setChips((prevChips) => {
            const nextChips = prevChips.filter((_, index) => index !== chipIndex)
            queueMicrotask(() => {
                onSearch(compileChipsToSearchString(nextChips, inputText))
            })
            if (nextChips.length === 0) {
                setFocusedElement(null)
                inputRef.current?.focus()
            } else {
                const nextFocusIndex = Math.min(chipIndex, nextChips.length - 1)
                setFocusedElement(nextFocusIndex)
                setTimeout(() => focusChip(nextFocusIndex), 0)
            }
            return nextChips
        })
    }, [inputText, onSearch])

    function deleteFocusedChip() {
        const activeElement = document.activeElement
        const chipRow = activeElement?.closest("[data-chip-row-index]")
        const chipIndex = parseInt(chipRow?.getAttribute("data-chip-row-index") ?? "-1", 10)
        if (chipIndex < 0) {
            return
        }
        removeChipAtIndex(chipIndex)
    }

    function navigateToPreviousChip() {
        const activeElement = document.activeElement
        if (activeElement === inputRef.current) {
            if (chips.length > 0) {
                focusChip(chips.length - 1)
            }
            return
        }
        const currentIndex = parseInt(
            activeElement?.closest("[data-chip-row-index]")?.getAttribute("data-chip-row-index") ?? "0",
            10
        )
        if (currentIndex > 0) {
            focusChip(currentIndex - 1)
        }
    }

    function navigateToNextChip() {
        const activeElement = document.activeElement
        if (activeElement === inputRef.current) {
            if (chips.length > 0) {
                focusChip(0)
            }
            return
        }
        const currentIndex = parseInt(
            activeElement?.closest("[data-chip-row-index]")?.getAttribute("data-chip-row-index") ?? "0",
            10
        )
        if (currentIndex < chips.length - 1) {
            focusChip(currentIndex + 1)
        } else {
            inputRef.current?.focus()
            inputRef.current?.setSelectionRange(0, 0)
        }
    }

    function applyChipFieldFocus(chipIndex, chip) {
        setFocusedElement(chipIndex)
        if (chip?.chipType === "dropdown") {
            const chipRef = chipRefs.current[chipIndex]
            if (chipRef?.openDropdown) {
                chipRef.openDropdown()
            } else {
                focusChip(chipIndex)
            }
            return
        }
        const chipRef = chipRefs.current[chipIndex]
        if (chipRef?.focusValueInput) {
            chipRef.focusValueInput()
            return
        }
        focusChip(chipIndex)
    }

    function scheduleChipFieldFocus(chipIndex, chip) {
        pendingChipFocusRef.current = { index: chipIndex, chip }
    }

    useLayoutEffect(() => {
        const pending = pendingChipFocusRef.current
        if (!pending) {
            return
        }
        pendingChipFocusRef.current = null
        const { index, chip } = pending
        // Run after refs attach and any portaled menu teardown from Filter...
        requestAnimationFrame(() => {
            applyChipFieldFocus(index, chip)
        })
    }, [chips])

    const addChip = useCallback((newChip) => {
        if (!newChip || typeof newChip !== "object") {
            return
        }
        setChips((prevChips) => {
            const existingIndex = prevChips.findIndex((chip) => chip.label === newChip.label)
            if (existingIndex !== -1) {
                requestAnimationFrame(() => {
                    applyChipFieldFocus(existingIndex, newChip)
                })
                return prevChips
            }
            const nextChips = [...prevChips, newChip]
            const nextIndex = nextChips.length - 1
            scheduleChipFieldFocus(nextIndex, newChip)
            return nextChips
        })
    }, [])

    useEffect(() => {
        if (onAddChip) {
            onAddChip(addChip)
        }
    }, [onAddChip, addChip])

    function handleInputKeyDown(event) {
        if (event.key === "ArrowLeft") {
            const cursorPosition = event.target.selectionStart
            if (cursorPosition === 0 && chips.length > 0) {
                event.preventDefault()
                focusChip(chips.length - 1)
            }
        } else if (event.key === "Backspace") {
            const cursorPosition = event.target.selectionStart
            if (cursorPosition === 0 && chips.length > 0) {
                event.preventDefault()
                focusChip(chips.length - 1)
            }
        } else if (event.key === "Enter") {
            event.preventDefault()
            submitSearch()
        }
    }

    function handleChipKeyDown(event, chipIndex) {
        const buttonElement = event.currentTarget
        const innerTextInput = buttonElement.querySelector("input[type=\"text\"]")
        if (document.activeElement === innerTextInput) {
            return
        }

        if (event.key === "ArrowLeft") {
            event.preventDefault()
            navigateToPreviousChip()
        } else if (event.key === "ArrowRight") {
            event.preventDefault()
            navigateToNextChip()
        } else if (event.key === "Enter") {
            event.preventDefault()
            const chip = chips[chipIndex]
            if (chip?.chipType === "dropdown") {
                chipRefs.current[chipIndex]?.openDropdown?.()
            } else if (innerTextInput) {
                innerTextInput.focus()
            }
        } else if (event.key === "Backspace") {
            event.preventDefault()
            deleteFocusedChip()
            if (chipIndex > 0) {
                focusChip(chipIndex - 1)
            } else if (chips.length > 1) {
                focusChip(chips.length - 2)
            }
        } else if (event.key === "Delete") {
            event.preventDefault()
            const wasLastChip = chipIndex === chips.length - 1
            deleteFocusedChip()
            if (wasLastChip) {
                inputRef.current?.focus()
                inputRef.current?.setSelectionRange(0, 0)
            } else {
                focusChip(chipIndex)
            }
        }
    }

    function handleChipRowFocusOut(event) {
        if (event.currentTarget.contains(event.relatedTarget)) {
            return
        }
        setFocusedElement(null)
    }

    function handleChipClick(event, chipIndex) {
        if (event.target.closest("[data-chip-remove-button]")) {
            return
        }
        if (event.target.closest("select")) {
            return
        }

        const chip = chips[chipIndex]
        if (chip?.chipType === "dropdown") {
            return
        }
        const valueInput = event.currentTarget.querySelector("[data-chip-value-input]")
        if (valueInput) {
            valueInput.focus()
            return
        }

        event.currentTarget.focus()
    }

    return (
        <div className={`position-relative ${className}`.trim()}>
            <Search
                className="position-absolute text-secondary"
                style={{
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                    pointerEvents: "none",
                }}
                size={20}
            />
            <div
                data-search-bar-container
                className="form-control ps-5 d-flex align-items-center gap-1"
                style={{
                    paddingLeft: "2.5rem",
                    paddingTop: "0.3125rem",
                    paddingBottom: "0.3125rem",
                    height: SEARCH_BAR_HEIGHT,
                    minHeight: SEARCH_BAR_HEIGHT,
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    overflowY: "hidden",
                }}
            >
                {chips.map((chip, index) => {
                    const chipProps = {
                        ref: (element) => {
                            chipRefs.current[index] = element
                        },
                        label: chip.label,
                        displayLabel: chip.displayLabel ?? chip.label,
                        command: chip.command ?? (chip.chipType === "dropdown" ? "=" : "contains"),
                        value: chip.value ?? "",
                        options: chip.options ?? [],
                        isHighlighted: focusedElement === index,
                    }

                    const isChipFocused = focusedElement === index

                    return (
                        <div
                            key={`${chip.label}-${index}`}
                            data-chip-row-index={index}
                            className="d-inline-flex align-items-center flex-shrink-0"
                            style={{
                                gap: `${FILTER_CHIP_REMOVE_GAP_PX}px`,
                                height: `${FILTER_CHIP_ROW_HEIGHT_PX}px`,
                                paddingRight: isChipFocused ?
                                    `${FILTER_CHIP_REMOVE_PADDING_RIGHT_PX}px` :
                                    0,
                            }}
                            onFocusCapture={() => setFocusedElement(index)}
                            onBlurCapture={handleChipRowFocusOut}
                        >
                            <div
                                data-chip-focus-target
                                data-button-index={index}
                                tabIndex={0}
                                style={{ outline: "none" }}
                                onKeyDown={(event) => handleChipKeyDown(event, index)}
                                onClick={(event) => handleChipClick(event, index)}
                            >
                                {chip.chipType === "dropdown" ? (
                                    <DropdownFilterChip
                                        {...chipProps}
                                        onValueChange={(newValue) => {
                                            handleDropdownChipValueChange(index, newValue)
                                        }}
                                    />
                                ) : (
                                    <TextFilterChip {...chipProps} />
                                )}
                            </div>
                            {isChipFocused ? (
                                <button
                                    type="button"
                                    data-chip-remove-button
                                    title="Remove filter"
                                    aria-label={`Remove ${chip.displayLabel ?? chip.label} filter`}
                                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0 border-0 bg-transparent p-0"
                                    style={{
                                        color: "#6c757d",
                                        height: `${FILTER_CHIP_ROW_HEIGHT_PX}px`,
                                        width: `${FILTER_CHIP_REMOVE_ICON_SIZE_PX}px`,
                                        cursor: "pointer",
                                    }}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        removeChipAtIndex(index)
                                    }}
                                >
                                    <X size={FILTER_CHIP_REMOVE_ICON_SIZE_PX} aria-hidden />
                                </button>
                            ) : null}
                        </div>
                    )
                })}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputText}
                    placeholder={chips.length === 0 && inputText === "" ? placeholder : ""}
                    onChange={(event) => setInputText(event.target.value)}
                    onKeyDown={handleInputKeyDown}
                    onFocus={() => setFocusedElement("input")}
                    onBlur={() => setFocusedElement(null)}
                    className="border-0 bg-transparent flex-grow-1"
                    style={{
                        outline: "none",
                        minWidth: chips.length > 0 ? "60px" : "0",
                        padding: 0,
                    }}
                />
            </div>
        </div>
    )
})

SearchBar.displayName = "SearchBar"

export default SearchBar
